import { useState, useEffect } from 'react';
import { Field, useFormikContext } from 'formik';
import { v4 as uuidv4 } from 'uuid';

import { InputUI, FormErrorUI, FormLabelUI, AutoCompleteSelectUI, DatePickerUI } from '../../../Interface';
import documentService from '../../../../service/newDoc.service';
import { generateAutocompleteItems } from '../../../../utils/helper';
import { DocumentType } from '../../../../config/DocumentType';
import conformityService from '../../../../service/client/varadiMetal/conformity.service';
import { doc } from 'prettier';

const ConformityFields = ({
  viewField,
  autocompleteOptions,
  autocompleteData,
  selectedValues,
  defConformityComponent,
}) => {
  const { values, errors, setFieldValue, validateField } = useFormikContext();

  const [docs, setDocs] = useState([]);
  const [docsOptions, setDocsOptions] = useState([]);

  const removeVals = () => {
    setFieldValue('baseId', null);
    setFieldValue('baseLine', null);
    setFieldValue('baseDocNum', '');
    setFieldValue('itemCode', null);
    setFieldValue('itemName', '');
    setFieldValue('quantityLabel', '');
    setFieldValue('orderNo', '');
    setFieldValue('conformityComponent', []);
  };

  useEffect(() => {
    if (!values.companyId) {
      setDocs([]);
      setDocsOptions([]);
      removeVals();
      return;
    }

    if (values.companyId) {
      documentService
        .getDocLinesByTypeAndCompany({
          companyId: values.companyId,
          docTypeId: DocumentType.CustomerOrder,
        })
        .then((data) => {
          setDocs(data);
          const docOptions = generateAutocompleteItems(data, ['docNum', 'itemCode', 'itemName', 'docReference'], 'id');
          setDocsOptions(docOptions);
        });
    }
  }, [values.companyId]);

  const handleFieldChange = async (field, newVal) => {
    const selected = newVal?.value ?? null;
    await setFieldValue(field, selected);
    await validateField(field);
  };

  const getConformityComponents = async (docId, lineNum) => {
    const components = await conformityService.getComponents(docId, lineNum);

    const newComponents = components.map((comp) => ({
      ...defConformityComponent,
      id: uuidv4(),
      stringAttr1: comp.f1,
      stringAttr2: comp.f2,
      stringAttr3: comp.f3,
    }));

    setFieldValue('conformityComponent', newComponents);
  };

  return (
    <div className="grid grid-cols-4 gap-x-4 mx-4 mb-5">
      <div className="col-span-2">
        <Field
          type="autocomplete"
          name="companyId"
          component={AutoCompleteSelectUI}
          fullWidth
          label={<FormLabelUI text="Partner" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.companyId} />}
          onChange={async (_e, newVal) => handleFieldChange('companyId', newVal)}
          disabled={viewField}
          InputLabelProps={{ shrink: Boolean(values.companyId) }}
          selectedValue={values.companyId}
          selectedLabelValue={
            autocompleteOptions?.company?.find((item) => item.value === values.companyId)?.label || ''
          }
          dataset={autocompleteOptions?.company}
        />
      </div>
      <div className="col-span-1">
        <Field
          name="baseDocNum"
          type="text"
          as={InputUI}
          fullWidth
          label={<FormLabelUI text="Bizonylat szám" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.baseDocNum} />}
          InputLabelProps={{ shrink: Boolean(values.baseDocNum) }}
          disabled={true}
        />
      </div>
      <div className="col-span-1">
        <Field
          name="orderNo"
          type="text"
          as={InputUI}
          fullWidth
          label={<FormLabelUI text="Rendelés száma" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.orderNo} />}
          InputLabelProps={{ shrink: Boolean(values.orderNo) }}
          disabled={viewField}
        />
      </div>
      <div className="col-span-3">
        <Field
          name="baseId"
          type="autocomplete"
          component={AutoCompleteSelectUI}
          fullWidth
          label={<FormLabelUI text="Bizonylat sor" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.baseId} />}
          onChange={async (_e, newVal) => {
            const selectedDocLine = docs.find((doc) => doc.id === newVal?.value);
            if (selectedDocLine) {
              await setFieldValue('baseId', selectedDocLine.docId);
              await setFieldValue('baseLine', selectedDocLine.lineNum);
              await setFieldValue('baseDocNum', selectedDocLine.docNum || '');
              await setFieldValue('itemCode', selectedDocLine.itemCode || '');
              await setFieldValue('itemName', selectedDocLine.itemName || '');
              await setFieldValue('quantityLabel', selectedDocLine.qty || 1);
              await setFieldValue('orderNo', selectedDocLine.docReference || '');

              await setFieldValue('qtyLabel', `${selectedDocLine.qty} ${selectedDocLine.quantityUnitCode}`);

              getConformityComponents(selectedDocLine.docId, selectedDocLine.lineNum);
            } else {
              removeVals();
            }
          }}
          disabled={viewField}
          InputLabelProps={{ shrink: Boolean(values.baseId) }}
          selectedValue={values.companyId ? `${values.baseId}_${values.baseLine}` : null}
          selectedLabelValue={
            docsOptions.find((item) => item.value === `${values.baseId}_${values.baseLine}`)?.label || ''
          }
          dataset={docsOptions ?? []}
        />
      </div>

      <div className="col-span-1">
        <Field
          name="standard"
          type="text"
          as={InputUI}
          fullWidth
          label={<FormLabelUI text="Szabvány" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.standard} />}
          InputLabelProps={{ shrink: Boolean(values.standard) }}
          disabled={viewField}
        />
      </div>
      <div className="col-span-1">
        <Field
          name="supplier"
          type="text"
          as={InputUI}
          fullWidth
          label={<FormLabelUI text="Beszállító" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.supplier} />}
          InputLabelProps={{ shrink: Boolean(values.supplier) }}
          disabled={viewField}
        />
      </div>

      <div className="col-span-1">
        <Field
          name="deliveryNo"
          type="text"
          as={InputUI}
          fullWidth
          label={<FormLabelUI text="Szállítólevél száma" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.deliveryNo} />}
          InputLabelProps={{ shrink: Boolean(values.deliveryNo) }}
          disabled={viewField}
        />
      </div>
      <div className="col-span-1">
        <Field
          type="date"
          name="deliveryDate"
          component={DatePickerUI}
          value={values.deliveryDate}
          fullWidth
          label={<FormLabelUI text="Szállítás dátuma" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.deliveryDate} />}
          onChange={(date) => {
            setFieldValue('deliveryDate', date).then(() => {
              validateField('deliveryDate');
            });

            setFieldValue('issueDate', date).then(() => {
              validateField('issueDate');
            });
          }}
          InputLabelProps={{ shrink: values.deliveryDate !== '' }}
          disabled={viewField}
        />
      </div>
      <div className="col-span-1">
        <Field
          name="qtyLabel"
          type="text"
          as={InputUI}
          fullWidth
          label={<FormLabelUI text="Mennyiség" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.qtyLabel} />}
          InputLabelProps={{ shrink: Boolean(values.qtyLabel) }}
          disabled={viewField}
        />
      </div>
      <div className="col-span-2">
        <Field
          name="itemCode"
          type="autocomplete"
          component={AutoCompleteSelectUI}
          fullWidth
          label={<FormLabelUI text="Cikk" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.itemCode} />}
          disabled={true}
          InputLabelProps={{ shrink: Boolean(values.itemCode) }}
          selectedValue={values.itemCode}
          selectedLabelValue={autocompleteOptions?.items?.find((item) => item.value === values.itemCode)?.label || ''}
          dataset={autocompleteOptions?.items}
        />
      </div>

      <div className="col-span-2">
        <Field
          name="itemDescription"
          type="text"
          as={InputUI}
          fullWidth
          label={<FormLabelUI text="Cikk megjegyzés" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.itemDescription} />}
          InputLabelProps={{ shrink: Boolean(values.itemDescription) }}
          disabled={viewField}
        />
      </div>

      <div className="col-span-2">
        <Field
          name="certificateNo"
          type="text"
          as={InputUI}
          fullWidth
          label={<FormLabelUI text="Jegyzőkönyvek" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.certificateNo} />}
          InputLabelProps={{ shrink: Boolean(values.certificateNo) }}
          disabled={viewField}
        />
      </div>
      <div className="col-span-1">
        <Field
          type="date"
          name="measureDate"
          component={DatePickerUI}
          value={values.measureDate}
          fullWidth
          label={<FormLabelUI text="Mérés dátuma" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.measureDate} />}
          onChange={(date) => {
            setFieldValue('measureDate', date).then(() => {
              validateField('measureDate');
            });
          }}
          disabled={viewField}
          InputLabelProps={{ shrink: values.measureDate !== '' }}
        />
      </div>
      <div className="col-span-1">
        <Field
          name="classification"
          type="text"
          as={InputUI}
          fullWidth
          label={<FormLabelUI text="Minősítés" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.classification} />}
          InputLabelProps={{ shrink: Boolean(values.classification) }}
          disabled={viewField}
        />
      </div>
      <div className="col-span-1">
        <Field
          name="issuePlace"
          type="text"
          as={InputUI}
          fullWidth
          label={<FormLabelUI text="Kiállítás helye" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.issuePlace} />}
          InputLabelProps={{ shrink: Boolean(values.issuePlace) }}
          disabled={true}
        />
      </div>
      <div className="col-span-1">
        <Field
          type="date"
          name="issueDate"
          component={DatePickerUI}
          value={values.issueDate}
          fullWidth
          label={<FormLabelUI text="Kiállítás dátuma" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.issueDate} />}
          onChange={(date) => {
            setFieldValue('issueDate', date).then(() => {
              validateField('issueDate');
            });
          }}
          disabled={viewField}
          InputLabelProps={{ shrink: values.measureDate !== '' }}
        />
      </div>
    </div>
  );
};

export default ConformityFields;
